import {
  CalendarDays,
  Clock,
  GraduationCap,
  MapPin,
  PartyPopper,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { ASSETS } from "../lib/constants";
import { useContent, resolveImage } from "../contexts/ContentContext";

export const SummerDiscovery = () => {
  const { data: summer } = useContent("summer");
  if (!summer) return null;

  const features = summer.features || [];
  const locations = summer.locations || [];
  const gradesSummary = (summer.grades || []).length > 0
    ? `${summer.grades[0]} – ${summer.grades[summer.grades.length - 3] || summer.grades[summer.grades.length - 1]} · ${summer.grades.slice(-2).join(" & ")}`
    : "";
  return (
    <section
      id="summer"
      data-testid="summer-discovery-section"
      className="relative py-20 md:py-28 bg-slate-50 overflow-hidden"
    >
      {/* Top divider */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <span className="section-eyebrow section-eyebrow-gold">
              <Sparkles className="w-3.5 h-3.5" />
              {summer.eyebrow}
            </span>
            <h2
              data-testid="summer-heading"
              className="mt-5 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900"
            >
              {summer.title}
            </h2>
            <p className="mt-5 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
              A five-week, fully-funded summer experience serving{" "}
              <span className="font-bold text-slate-900">800+ youth</span> while
              creating meaningful summer employment for teachers, paraprofessionals,
              and community staff.
            </p>
            {summer.signup_url && (
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a
                  href={summer.signup_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="summer-signup-cta"
                  className="inline-flex items-center gap-2 bg-[#FFD100] hover:bg-[#FFDB33] text-[#0F172A] font-bold px-6 py-3.5 rounded-full shadow-[0_10px_30px_rgba(255,209,0,0.35)] hover:-translate-y-0.5 transition-all"
                >
                  <Sparkles className="w-4 h-4" strokeWidth={2.4} />
                  {summer.signup_label || "Sign up your child"}
                  <ArrowRight className="w-4 h-4" />
                </a>
                <span className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
                  Free · Grades {(summer.grades || [])[0]?.replace("Rising ", "") || "1"}–{(summer.grades || []).slice(-1)[0] || "10"}
                </span>
              </div>
            )}
          </div>
          <div className="lg:col-span-4 flex justify-start lg:justify-end">
            <img
              src={ASSETS.summerDiscoveryLogo}
              alt="DiscoverWorks™"
              data-testid="summer-discovery-logo"
              className="w-56 sm:w-72 md:w-80 h-auto"
              loading="lazy"
            />
          </div>
        </div>

        {/* Bento grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {/* Big feature card with flyer */}
          <div
            className="md:col-span-7 relative rounded-3xl overflow-hidden min-h-[360px] md:min-h-[480px] group"
            data-testid="summer-flyer-card"
          >
            <img
              src={ASSETS.flyerBack}
              alt="DiscoverWorks™ program with locations"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-tr from-[#0F172A]/85 via-[#1D4ED8]/55 to-transparent"
            />
            <div className="relative h-full flex flex-col justify-end p-6 md:p-10">
              <span className="inline-flex items-center gap-1.5 self-start bg-[#FFD100] text-[#0F172A] text-xs font-bold px-3 py-1.5 rounded-full">
                {summer.cost}
              </span>
              <h3 className="mt-4 font-display text-3xl md:text-5xl font-extrabold text-white tracking-tight">
                {summer.dates}
              </h3>
              <p className="mt-2 text-white/85 text-sm md:text-base">
                Program hours {summer.hours} • Grant Recipient Program
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {features.slice(0, 6).map((f) => (
                  <span
                    key={f}
                    className="bg-white/15 backdrop-blur text-white text-xs px-3 py-1.5 rounded-full border border-white/20"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="md:col-span-5 grid grid-cols-1 gap-5 md:gap-6">
            <div className="card-soft p-6 md:p-7 flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl bg-[#FFD100]/20 grid place-items-center text-[#B88800] shrink-0">
                <CalendarDays className="w-6 h-6" strokeWidth={2.2} />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Dates
                </p>
                <p className="mt-1 font-display text-xl font-bold text-slate-900">
                  {summer.dates}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  Five weeks of discovery, learning & joy.
                </p>
              </div>
            </div>
            <div className="card-soft p-6 md:p-7 flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl bg-[#2563EB]/10 grid place-items-center text-[#2563EB] shrink-0">
                <Clock className="w-6 h-6" strokeWidth={2.2} />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Hours
                </p>
                <p className="mt-1 font-display text-xl font-bold text-slate-900">
                  {summer.hours}, Mon–Fri
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  Full-day enrichment & supervised care.
                </p>
              </div>
            </div>
            <div className="card-soft p-6 md:p-7 flex items-start gap-4">
              <span className="w-12 h-12 rounded-2xl bg-[#2563EB]/10 grid place-items-center text-[#2563EB] shrink-0">
                <GraduationCap className="w-6 h-6" strokeWidth={2.2} />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Grades served
                </p>
                <p className="mt-1 font-display text-xl font-bold text-slate-900">
                  {gradesSummary || (summer.grades || []).join(", ")}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  Tailored programming by age group.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Locations */}
        <div className="mt-10 md:mt-14">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <h3 className="font-display text-2xl md:text-3xl font-bold text-slate-900">
              Program locations
            </h3>
          </div>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {locations.map((loc, idx) => {
              const logoUrl = resolveImage(loc.logo);
              const hasSignup = !!loc.signup_url;
              const hasUrl = !!loc.url;

              // When the final row has a single card (total % 4 === 1), that card
              // spans the full width on lg so the row never looks empty.
              const isOrphanLast =
                idx === locations.length - 1 &&
                locations.length % 4 === 1 &&
                locations.length > 1;

              // Inner tile contents — compact vertical layout for grid cards.
              const compactInner = (
                <>
                  {logoUrl ? (
                    <div className="w-14 h-14 rounded-xl bg-white ring-1 ring-slate-100 grid place-items-center p-1.5">
                      <img src={logoUrl} alt={`${loc.name} logo`} className="w-full h-full object-contain" loading="lazy" />
                    </div>
                  ) : (
                    <span className="inline-flex w-14 h-14 rounded-xl bg-[#2563EB]/10 text-[#2563EB] items-center justify-center">
                      <MapPin className="w-6 h-6" strokeWidth={2.4} />
                    </span>
                  )}
                  <p className="mt-4 font-display text-lg font-bold text-slate-900 leading-tight">{loc.name}</p>
                  <p className="text-sm text-slate-600 mt-1.5 leading-relaxed flex-1">{loc.address}</p>
                  <div className="mt-4 flex items-center justify-between gap-2">
                    <div className="inline-flex items-center text-xs font-semibold text-[#2563EB] bg-[#2563EB]/8 px-3 py-1.5 rounded-full">
                      {loc.grades}
                    </div>
                    {hasUrl && (
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1D4ED8] group-hover:translate-x-0.5 transition-all shrink-0" />
                    )}
                  </div>
                </>
              );

              // Expanded banner layout for the orphan last card — horizontal split.
              const bannerInner = (
                <div className="flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-8">
                  {logoUrl ? (
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white ring-1 ring-slate-100 grid place-items-center p-2 shrink-0">
                      <img src={logoUrl} alt={`${loc.name} logo`} className="w-full h-full object-contain" loading="lazy" />
                    </div>
                  ) : (
                    <span className="inline-flex w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-[#2563EB]/10 text-[#2563EB] items-center justify-center shrink-0">
                      <MapPin className="w-10 h-10" strokeWidth={2.2} />
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-xl md:text-2xl font-bold text-slate-900 leading-tight">{loc.name}</p>
                    <p className="text-sm md:text-base text-slate-600 mt-1.5 leading-relaxed">{loc.address}</p>
                    <div className="mt-3 inline-flex items-center text-xs font-semibold text-[#2563EB] bg-[#2563EB]/8 px-3 py-1.5 rounded-full">
                      {loc.grades}
                    </div>
                  </div>
                  {hasSignup ? (
                    <a
                      href={loc.signup_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`location-signup-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="shrink-0 self-stretch md:self-auto inline-flex items-center justify-center gap-2 bg-[#FFD100] hover:bg-[#FFDB33] text-[#0F172A] font-bold text-sm md:text-base px-5 py-3 rounded-full shadow-[0_10px_30px_rgba(255,209,0,0.35)] hover:-translate-y-0.5 transition-all"
                    >
                      {loc.signup_label || "Sign up now"}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : hasUrl ? (
                    <a
                      href={loc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#0F172A] hover:bg-[#1E3A8A] text-white font-bold text-sm px-5 py-3 rounded-full transition-all"
                    >
                      Visit website <ArrowRight className="w-4 h-4" />
                    </a>
                  ) : null}
                </div>
              );

              // Wrapper varies: when the card has its own sign-up button and we're
              // NOT in banner mode (which already renders the button), keep the card
              // as <div> so the button stays an independent anchor.
              const ShellTag = !hasSignup && hasUrl && !isOrphanLast ? "a" : "div";
              const shellProps = !hasSignup && hasUrl && !isOrphanLast
                ? { href: loc.url, target: "_blank", rel: "noopener noreferrer" }
                : {};

              return (
                <ShellTag
                  key={loc.name}
                  {...shellProps}
                  data-testid={`location-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`card-soft p-6 md:p-7 flex flex-col group ${
                    isOrphanLast ? "sm:col-span-2 lg:col-span-4" : ""
                  }`}
                >
                  {isOrphanLast ? bannerInner : (
                    <>
                      {compactInner}
                      {hasSignup && (
                        <a
                          href={loc.signup_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`location-signup-${loc.name.toLowerCase().replace(/\s+/g, "-")}`}
                          className="mt-4 inline-flex items-center justify-center gap-1.5 bg-[#FFD100] hover:bg-[#FFDB33] text-[#0F172A] font-bold text-sm px-4 py-2.5 rounded-full shadow-[0_8px_20px_rgba(255,209,0,0.35)] hover:-translate-y-0.5 transition-all"
                        >
                          {loc.signup_label || "Sign up now"}
                          <ArrowRight className="w-4 h-4" />
                        </a>
                      )}
                    </>
                  )}
                </ShellTag>
              );
            })}
          </div>
        </div>

        {/* Main Program Partners highlight */}
        {(summer.main_partners?.items?.length ?? 0) > 0 && (
        <div
          data-testid="main-partners-highlight"
          className="mt-12 md:mt-16 rounded-[28px] bg-gradient-to-br from-[#1E3A8A] via-[#1D4ED8] to-[#2563EB] text-white p-7 md:p-10 grid md:grid-cols-12 gap-8 items-center relative overflow-hidden"
        >
          <div aria-hidden="true" className="absolute -bottom-16 -left-16 w-64 h-64 rounded-full bg-[#FFD100]/20 blur-3xl" />
          <div className="md:col-span-5 relative">
            <p className="text-[#FFD100] text-xs font-bold uppercase tracking-[0.2em]">{summer.main_partners.eyebrow}</p>
            <h3 className="mt-3 font-display text-2xl md:text-4xl font-bold leading-tight">{summer.main_partners.heading}</h3>
            <p className="mt-3 text-white/80 text-sm md:text-base max-w-md">{summer.main_partners.intro}</p>
          </div>
          <div className="md:col-span-7 relative grid grid-cols-2 gap-4">
            {summer.main_partners.items.map((p, i) => {
              const Shell = p.url ? "a" : "div";
              const props = p.url ? { href: p.url, target: "_blank", rel: "noopener noreferrer" } : {};
              return (
                <Shell
                  key={i}
                  {...props}
                  data-testid={`main-partner-${i}`}
                  className="bg-white rounded-2xl aspect-[4/3] grid place-items-center p-6 hover:-translate-y-1 transition-transform shadow-[0_20px_50px_rgba(0,0,0,0.15)]"
                >
                  {p.logo ? (
                    <img src={resolveImage(p.logo)} alt={p.name} className="w-full h-full object-contain max-h-24" loading="lazy" />
                  ) : (
                    <span className="font-display text-2xl font-extrabold text-[#2563EB]">{p.name}</span>
                  )}
                </Shell>
              );
            })}
          </div>
        </div>
        )}

        {/* Official Main Partner(s) */}
        {(() => {
          const om = summer.official_main_partner;
          if (!om) return null;
          // Backward-compat: older stored docs had a single `item`; new schema has `items` list.
          const items = om.items && om.items.length ? om.items : (om.item ? [om.item] : []);
          if (items.length === 0) return null;
          return (
            <div
              data-testid="summer-official-main-partner"
              className="mt-6 rounded-[28px] bg-white border border-slate-100 shadow-[0_10px_40px_rgba(15,23,42,0.06)] p-6 md:p-8 grid md:grid-cols-12 gap-6 items-center"
            >
              <div className="md:col-span-4">
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#1D4ED8]">{om.eyebrow}</p>
                <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight">{om.heading}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-snug">{om.intro}</p>
              </div>
              <div className={`md:col-span-8 grid gap-4 ${items.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
                {items.map((it, i) => {
                  const Shell = it.url ? "a" : "div";
                  const props = it.url ? { href: it.url, target: "_blank", rel: "noopener noreferrer" } : {};
                  return (
                    <Shell
                      key={i}
                      {...props}
                      data-testid={`official-main-partner-${i}`}
                      className={`bg-slate-50 rounded-2xl grid place-items-center p-6 ring-1 ring-slate-100 hover:-translate-y-1 transition-transform ${items.length === 1 ? "aspect-[16/6]" : "aspect-[4/3]"}`}
                    >
                      {it.logo ? (
                        <img src={resolveImage(it.logo)} alt={it.name} className="w-full h-full object-contain max-h-24 md:max-h-28" loading="lazy" />
                      ) : (
                        <span className="font-display text-2xl font-extrabold text-[#2563EB]">{it.name}</span>
                      )}
                    </Shell>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Official Gear strip */}
        {summer.merch?.title && (
        <div data-testid="summer-community-strip" className="mt-6">
          <div data-testid="summer-merch-card" className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#FFD100] via-[#F59E0B] to-[#FFD100] p-1">
            <div className="relative rounded-[22px] bg-white h-full grid grid-cols-[auto,1fr] items-center gap-5 p-5 md:p-6">
              <div className="w-28 h-28 md:w-32 md:h-32 shrink-0">
                {summer.merch.image ? (
                  <img src={resolveImage(summer.merch.image)} alt={summer.merch.title} className="w-full h-full object-contain" loading="lazy" />
                ) : (
                  <div className="w-full h-full rounded-xl bg-slate-100" />
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#B88800]">{summer.merch.eyebrow}</p>
                <p className="mt-1 font-display text-lg md:text-2xl font-bold text-slate-900 leading-tight">{summer.merch.title}</p>
                <p className="mt-1.5 text-sm md:text-base text-slate-600 leading-snug">{summer.merch.description}</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Official Food Partners */}
        {(summer.food_partners?.items?.length ?? 0) > 0 && (
        <div
          data-testid="summer-food-partners"
          className="mt-6 rounded-[28px] bg-white border border-slate-100 shadow-[0_10px_40px_rgba(15,23,42,0.06)] p-6 md:p-8 grid md:grid-cols-12 gap-6 items-center"
        >
          <div className="md:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#B88800]">{summer.food_partners.eyebrow}</p>
            <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold text-slate-900 leading-tight">{summer.food_partners.heading}</h3>
            <p className="mt-2 text-sm text-slate-600 leading-snug">{summer.food_partners.intro}</p>
          </div>
          <div className="md:col-span-8 grid grid-cols-2 gap-4">
            {summer.food_partners.items.map((p, i) => {
              const Shell = p.url ? "a" : "div";
              const props = p.url ? { href: p.url, target: "_blank", rel: "noopener noreferrer" } : {};
              return (
                <Shell
                  key={i}
                  {...props}
                  data-testid={`food-partner-${i}`}
                  className="bg-slate-50 rounded-2xl aspect-[4/3] grid place-items-center p-6 ring-1 ring-slate-100 hover:-translate-y-1 transition-transform"
                >
                  {p.logo ? (
                    <img src={resolveImage(p.logo)} alt={p.name} className="w-full h-full object-contain max-h-24" loading="lazy" />
                  ) : (
                    <span className="font-display text-xl font-extrabold text-[#2563EB]">{p.name}</span>
                  )}
                </Shell>
              );
            })}
          </div>
        </div>
        )}

        {/* Features marquee */}
        <div className="mt-12 md:mt-16 -mx-6 md:-mx-10 overflow-hidden">
          <div className="flex marquee-track gap-3 w-max">
            {[...features, ...features].map((f, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-100 text-sm font-medium text-slate-700 shadow-[0_4px_14px_rgba(15,23,42,0.04)]"
              >
                <PartyPopper className="w-4 h-4 text-[#FFD100] fill-[#FFD100]" />
                {f}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
